/**
 * Procedures page — Release 2A of the Team-Task-Training spec.
 *
 * Lists every non-archived procedure. Non-managers only see procedures
 * that have a published version.
 *
 * Managers/Owners can:
 *  - Create a new procedure
 *  - Click through to /procedures/:id to draft a new version and publish
 *  - Archive a procedure
 *
 * All authenticated users can:
 *  - Search by title/category
 *  - See which procedures they still need to acknowledge (pill on the row)
 *  - Click through to read the current published version + acknowledge
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BookOpen, Plus, Loader2, ArrowRight, Search, CheckCircle2, AlertCircle,
} from "lucide-react";

export default function Procedures({ embedded = false }) {
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rProc, rPending] = await Promise.all([
        api.get("/procedures"),
        api.get("/procedures/pending-acknowledgements"),
      ]);
      setRows(rProc.data || []);
      setPending(rPending.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingIds = useMemo(
    () => new Set(pending.map((p) => p.procedure_id)),
    [pending],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.title || "").toLowerCase().includes(needle)
      || (r.category || "").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  return (
    <div className={embedded ? "space-y-4" : "max-w-6xl mx-auto p-4 space-y-4"} data-testid="procedures-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          {!embedded && (
            <>
              <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
                <BookOpen className="h-6 w-6" /> Procedures
              </h1>
              <p className="text-xs text-zinc-500 mt-0.5">
                Clinic SOPs. Managers publish new versions; every staff member
                acknowledges each new version.
              </p>
            </>
          )}
        </div>
        {isManager && (
          <Button
            onClick={() => setShowCreate(true)}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="procedures-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New procedure
          </Button>
        )}
      </div>

      {pending.length > 0 && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-sm text-amber-900"
          data-testid="procedures-pending-banner"
        >
          <AlertCircle className="h-4 w-4" />
          <span>
            You have <b>{pending.length}</b> procedure{pending.length === 1 ? "" : "s"} to acknowledge.
          </span>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title or category…"
          className="pl-8"
          data-testid="procedures-search"
        />
      </div>

      {loading ? (
        <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
          {rows.length === 0
            ? (isManager ? "No procedures yet — click ‘New procedure’ to start." : "No procedures have been published yet.")
            : "No procedures match your search."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => (
            <ProcedureRow key={p.id} proc={p} pending={pendingIds.has(p.id)} />
          ))}
        </div>
      )}

      {showCreate && (
        <NewProcedureDialog
          onClose={() => setShowCreate(false)}
          onCreated={(newProc) => {
            setShowCreate(false);
            load();
            // Optional: send them straight into the editor for the first draft
            if (newProc?.id) {
              window.location.href = `/procedures/${newProc.id}`;
            }
          }}
        />
      )}
    </div>
  );
}


function ProcedureRow({ proc, pending }) {
  const published = proc.current_published_version;
  return (
    <Link
      to={`/procedures/${proc.id}`}
      className="block border border-zinc-200 rounded-md p-3 hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
      data-testid={`procedure-row-${proc.id}`}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-950 flex items-center gap-2 flex-wrap">
            {proc.title}
            {proc.category && (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200">
                {proc.category}
              </span>
            )}
            {published ? (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                v{published} published
              </span>
            ) : (
              <span className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 border border-zinc-200">
                draft only
              </span>
            )}
            {pending && (
              <span
                className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300"
                data-testid={`procedure-pending-${proc.id}`}
              >
                needs your ack
              </span>
            )}
          </div>
          {proc.description && (
            <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{proc.description}</div>
          )}
        </div>
        <ArrowRight className="h-4 w-4 text-zinc-400 mt-1" />
      </div>
    </Link>
  );
}


function NewProcedureDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", category: "", description: "" });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const r = await api.post("/procedures", {
        title: form.title.trim(),
        category: form.category.trim() || null,
        description: form.description,
      });
      toast.success("Procedure created — draft the first version next.");
      onCreated?.(r.data);
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-procedure-dialog">
        <DialogHeader>
          <DialogTitle>New procedure</DialogTitle>
          <DialogDescription className="text-xs">
            Set the name and a short description. You&apos;ll draft the actual
            content on the next screen and publish it when it&apos;s ready.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Fire evacuation procedure"
              data-testid="new-procedure-title"
            />
          </div>
          <div>
            <Label className="text-2xs">Category</Label>
            <Input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. Safety, Clinical, IT"
              data-testid="new-procedure-category"
            />
          </div>
          <div>
            <Label className="text-2xs">Short description</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="new-procedure-description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-zinc-950 text-white"
            data-testid="new-procedure-save"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Create procedure"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
